import { gql } from '@apollo/client';
import { getFormatterRequiredFields } from '@spoiler-alert/ui-library';
import { OfferCompRequiredFields, REQUIRED_PATHS } from '../../graphql/fragments/segmented-listings-base';
import checkFeature from '../../helpers/check-feature-flag';
import featureFlags from '../../enums/feature-flags';

const PROFILE_NAME = 'Offer Comp';

// The desirability column is inserted client-side (offer-comparison-table.tsx) rather than
// configured in the site's profile, so collectRequiredPaths never asks for these -- without them
// the indicator's formatter sees an undefined `desirability` and renders nothing. Selected only
// when the flag is on so sites without the feature keep the narrower payload.
// The server's `isFlagged` is deliberately omitted: the presenter keys off `assessment`.
// The reserve-price-exception suggestion is here too, not in the required fragment, because the
// indicator only renders for rows that have an Opportunity to act on. `offerListings.status`, the
// other half of that test, is already in OfferCompRequiredFields.
const DESIRABILITY_PATHS = [
  'desirability.assessment',
  'desirability.shelfLife.status',
  'desirability.shelfLife.actual',
  'desirability.overlap.status',
  'desirability.overlap.actual',
  'desirability.cycleIndex.status',
  'desirability.cycleIndex.actual',
  'offerListings.suggestions.reservePriceException.suggested',
];

// Related fields that a formatter may read but that do not exist on the Offer Comp row shape
// (the query returns inventories, each with a `site` object and an `offerListings` array).
const UNSUPPORTED_RELATIONS = ['transaction', 'negotiation', 'negotiationListing', 'inventory', 'metaData', 'offerDetails'];

// Map a profile column's structured baseType/path to a row-relative field path matching the query
// result shape. Computed columns are derived client-side and are not queryable.
const columnToRowPath = (column) => {
  const path = column.path || column.field;
  switch (column.baseType) {
    case 'Inventory':
      return path;
    case 'Site':
      return `site.${path}`;
    case 'OfferListing':
      return `offerListings.${path}`;
    case 'Computed':
      return null;
    default:
      return path; // legacy/untagged columns: assume a top-level inventory field
  }
};

// Map a formatter dependency (row-relative, as the formatter reads it) onto the query result shape.
// Formatters reference the singular `offerListing`; the query exposes the `offerListings` array.
const formatterDepToRowPath = (dep) => {
  if (dep === 'offerListing') return null;
  if (dep.startsWith('offerListing.')) return `offerListings.${dep.slice('offerListing.'.length)}`;
  if (UNSUPPORTED_RELATIONS.includes(dep.split('.')[0])) return null;
  return dep;
};

// Build a nested GraphQL selection-set string from a list of dotted paths.
const buildSelectionSet = (paths) => {
  const tree = {};
  paths.forEach((path) => {
    if (!path) return;
    let node = tree;
    path.split('.').forEach((segment) => {
      node[segment] = node[segment] || {};
      // noopengrep: javascript.lang.security.audit.prototype-pollution.prototype-pollution-loop.prototype-pollution-loop
      node = node[segment];
    });
  });
  const render = (node) =>
    Object.entries(node)
      .map(([key, child]) => (Object.keys(child).length ? `${key} { ${render(child)} }` : key))
      .join(' ');
  return render(tree);
};

// The column's own leaf field name. getFormatterRequiredFields also emits this bare leaf (mirroring
// buildColumnFormatter's `row[field]` fallback), but its correct location is already handled by
// columnToRowPath — so the builder ignores the bare leaf to avoid mis-placing it at the row root.
const columnLeaf = (column) => {
  if (column.path) return column.path.split('.').pop();
  if (column.field) return column.field.split('_').pop();
  return undefined;
};

/**
 * Collects the row-relative field paths a set of Offer Comp profile columns needs — each column's
 * own baseType/path plus every field its formatter chain reads (see getFormatterRequiredFields).
 */
export const collectRequiredPaths = (columns = []) => {
  const paths = new Set();
  columns.forEach((column) => {
    const own = columnToRowPath(column);
    if (own) paths.add(own);
    const leaf = columnLeaf(column);
    getFormatterRequiredFields(column).forEach((dep) => {
      if (dep === leaf) return; // column's own field — already placed correctly by columnToRowPath
      const mapped = formatterDepToRowPath(dep);
      if (mapped) paths.add(mapped);
    });
  });
  return paths;
};

/**
 * Builds the SegmentedActiveListingsQuery dynamically from the site's "Offer Comp" data table
 * profile, selecting only the fields needed to render the page (the required fragment + the
 * profile-driven columns and their formatter dependencies) instead of the wide static fragments,
 * plus any feature-flagged selections whose columns are inserted client-side.
 *
 * The flags are read here rather than passed in so the query cannot drift from the columns that
 * need it. Like those columns, a flag flipping mid-session does not rebuild the query -- the caller
 * memoises on `user.site`, and features are loaded before the page mounts (internal-layout.jsx).
 */
export const buildSegmentedListingsQuery = (site) => {
  const profile = site?.dataTableProfiles?.find((p) => p.dataTableName === PROFILE_NAME);
  const dynamicPaths = collectRequiredPaths(profile?.columns);

  if (checkFeature(featureFlags.lowDesirabilityFlag)) DESIRABILITY_PATHS.forEach((path) => dynamicPaths.add(path));

  // Omit anything the required fragment already selects to keep the query clean.
  const required = new Set(REQUIRED_PATHS);
  const additions = [...dynamicPaths].filter((path) => !required.has(path));
  const selectionSet = buildSelectionSet(additions);

  return gql`
    ${OfferCompRequiredFields}
    query listedInventoriesQuery($limit: Int!, $fetchMoreAfter: String) {
      listedInventoriesQuery(limit: $limit, fetchMoreAfter: $fetchMoreAfter) {
        ...OfferCompRequiredFields
        ${selectionSet}
      }
    }
  `;
};
